import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  exhaustMap,
  filter,
  first,
  Observable,
  of,
  switchMap,
  throwError,
  timer,
} from 'rxjs';
import { HttpService } from '../http.service';
import { AskAiResult, AskAiService, AskAiStatus } from './ask-ai.service';

interface CreateAskAiRequest {
  question: string;
  patientId: string | null;
}

interface CreateAskAiResponse {
  id: string;
  status: AskAiStatus;
}

interface AskAiStatusResponse {
  id: string;
  status: AskAiStatus;
  createdAtUtc: string;
  startedAtUtc: string | null;
  completedAtUtc: string | null;
  failedAtUtc: string | null;
  errorMessage: string | null;
}

const POLL_INTERVAL_MS = 2000;

@Injectable({ providedIn: 'root' })
export class AskAiApiService
  extends HttpService<AskAiResult>
  implements AskAiService
{
  protected override readonly collectionPath = '/ask-ai';

  ask(question: string, patientId?: string | null): Observable<AskAiResult> {
    const body: CreateAskAiRequest = { question, patientId: patientId ?? null };
    return this.http
      .post<CreateAskAiResponse>(this.apiUrl, body)
      .pipe(switchMap(({ id }) => this.pollUntilDone(id)));
  }

  getPdf(id: string): Observable<Blob> {
    const params = new HttpParams().set('action', 'Download');
    return this.http.get(`${this.apiUrl}/${id}/pdf`, {
      params,
      responseType: 'blob',
    });
  }

  private pollUntilDone(id: string): Observable<AskAiResult> {
    const pendingStatuses: AskAiStatus[] = ['Queued', 'Processing'];

    return timer(0, POLL_INTERVAL_MS).pipe(
      exhaustMap(() =>
        this.http.get<AskAiStatusResponse | AskAiResult>(
          `${this.apiUrl}/${id}/status`
        )
      ),
      filter(({ status }) => !pendingStatuses.includes(status)),
      first(),
      switchMap(response => {
        if (response.status === 'Failed') {
          return throwError(() => new Error('Ask AI generation failed'));
        }
        if ('answerText' in response) {
          return of(response);
        }
        return this.getItemById<AskAiResult>(id);
      })
    );
  }
}
